import {
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  BgColorsOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  DownOutlined,
  FolderOutlined,
  LockOutlined,
  LeftOutlined,
  RightOutlined,
  PartitionOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  TruckOutlined,
  UpOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Dropdown, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import TraidsterLogoDark from "../../assets/img/brand/traidster-logo-dark.png";
import TOpsLogo from "../../assets/img/brand/t-ops-logo-dark.png";
import TOpsMark from "../../assets/img/brand/t-ops-mark.png";
import { loadMenuFromAPI } from "../../store/actions/menu";
import { getOrganizations } from "../../services/SassServices/OrganizationService";
import { authLogout } from "../../store/actions/user";
import * as PC from "../../config/pageCodes";
import { useTransparentLogo } from "../../hooks/useTransparentLogo";

/* ─── icon registry ───────────────────────────────────────────── */
const ICON_MAP = {
  dashboard: <DashboardOutlined />,
  bar_chart: <BarChartOutlined />,
  settings: <SettingOutlined />,
  apartment: <PartitionOutlined />,
  person: <UserOutlined />,
  team: <TeamOutlined />,
  lock: <LockOutlined />,
  dollar: <DollarOutlined />,
  database: <DatabaseOutlined />,
  appstore: <AppstoreOutlined />,
  shop: <ShopOutlined />,
  truck: <TruckOutlined />,
  bg_colors: <BgColorsOutlined />,
  folder: <FolderOutlined />,
  audit: <AuditOutlined />,
};
const getIcon = (key) => ICON_MAP[key] || <AppstoreOutlined />;

/* ─── helpers ─────────────────────────────────────────────────── */
// eslint-disable-next-line no-unused-vars
function isAccessible(page, pageAccess) {
  return true;
}

function getModuleLeafRoutes(module, pageAccess) {
  const routes = [];
  for (const p of module.directPages || []) {
    if (p.isActive && isAccessible(p, pageAccess)) routes.push(p.route);
  }
  for (const sub of module.submodules || []) {
    if (!sub.isActive) continue;
    for (const p of sub.pages || []) {
      if (p.isActive && isAccessible(p, pageAccess)) routes.push(p.route);
    }
  }
  return routes;
}

/* ─── Fulfilment Center (FC) users ───────────────────────────────
   FC users (userDetails.fcId present) only handle physical fulfilment —
   restrict the sidebar to Order Dashboard (Instant Fulfillment) + the
   full Fulfillment Center submodule (Products Movement, Money Movement,
   DPP Ledger). Everything else (other modules, Lead Dashboard, Create
   Manual Order, Future Fulfillment) is hidden for this user type only. */
const FC_SUBMODULE_ALLOW = {
  70: ["/order_listing"], // Instant Fulfillment → Order Dashboard only
  72: null, // Fulfillment Center → keep all pages
};

function getFcModules(modules) {
  const fulfillModule = modules.find((m) => m.moduleCode === PC.MOD_FULFILL);
  if (!fulfillModule) return [];

  const submodules = (fulfillModule.submodules || [])
    .filter((sub) =>
      Object.prototype.hasOwnProperty.call(FC_SUBMODULE_ALLOW, sub.id),
    )
    .map((sub) => {
      const allowedRoutes = FC_SUBMODULE_ALLOW[sub.id];
      if (!allowedRoutes) return sub;
      return {
        ...sub,
        pages: (sub.pages || []).filter((p) => allowedRoutes.includes(p.route)),
      };
    });

  return [{ ...fulfillModule, directPages: [], submodules }];
}

/* Below this the rail is replaced by a burger-toggled drawer (keep in sync
   with the matching @media block in style.css) */
const MOBILE_QUERY = "(max-width: 767px)";

/* ─── localStorage helpers ────────────────────────────────────── */
const LS_COLLAPSED = "sb_collapsed";
function readCollapsed() {
  try {
    const v = localStorage.getItem(LS_COLLAPSED);
    return v === null ? false : v === "true";
  } catch {
    return false;
  }
}
function writeCollapsed(val) {
  try {
    localStorage.setItem(LS_COLLAPSED, String(val));
  } catch {
    /* ignore */
  }
}

/* ═══════════════════════════════════════════════════════════════ */
const Sidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const mode = useSelector((s) => s.ui.mode);
  const menuData = useSelector((s) => s.menu.menuData);
  const menuLoading = useSelector((s) => s.menu.menuLoading);
  const pageAccess = useSelector(
    (s) => s.User.pageAccessDetails?.allowedPages || [],
  );
  const openKeys = useSelector((s) => s.ui.sidebarOpenKeys);
  const userDetails = useSelector((s) => s.User.userDetails);
  const isPlatformUser = userDetails?.orgType === "PLATFORM";
  const isFcUser = userDetails?.fcId != null;
  const [brandLogo, setBrandLogo] = useState(null);

  // Logged-in org's logo (AT-7139). getOrganizations() filters by
  // `organizationIds` — passing anything else silently returns ALL orgs,
  // and taking [0] then showed another client's logo. Match the org by id
  // too, so a missing/ignored filter can never show the wrong client's
  // logo; refetch (and clear the old one first) when the org changes.
  const ownOrgId = userDetails?.orgId;
  useEffect(() => {
    setBrandLogo(null);
    if (!ownOrgId) return undefined;
    let cancelled = false;
    getOrganizations({ organizationIds: ownOrgId })
      .then((response) => {
        if (cancelled) return;
        const org = (response?.data?.organizations || []).find(
          (o) =>
            String(o.orgId ?? o.organizationId ?? o.id) === String(ownOrgId),
        );
        setBrandLogo(org?.logoUrl || null);
      })
      .catch((error) => console.error("Failed to fetch organization:", error));
    return () => {
      cancelled = true;
    };
  }, [ownOrgId]);

  // Best-effort: strip a near-white background baked into the uploaded
  // logo file itself (see useTransparentLogo's own doc comment). Falls
  // back to brandLogo untouched on any failure — .sb-brand-box--logo's
  // solid white background is the real safety net either way.
  const transparentBrandLogo = useTransparentLogo(brandLogo);

  /* brand */
  // eslint-disable-next-line no-unused-vars
  // const brandLogo = userDetails?.orgLogo || PurpleTradeLogo;

  const brandName = isPlatformUser
    ? "Traidster"
    : userDetails?.orgName || userDetails?.organizationName || "Ops Portal";

  /* user info */
  const userName =
    userDetails?.name ||
    userDetails?.user_name ||
    userDetails?.firstName ||
    "User";
  const userInitial = String(userName)[0].toUpperCase();

  /* ── small screens: sidebar becomes an off-canvas drawer behind a burger ── */
  const [isMobile, setIsMobile] = useState(() =>
    window.matchMedia(MOBILE_QUERY).matches,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) setMobileOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  /* ── sidebar collapse ───────────────────────────────────────── */
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const doSetCollapsed = (val) => {
    setCollapsed(val);
    writeCollapsed(val);
  };
  const toggleCollapse = () => doSetCollapsed(!collapsed);

  /* ── active panel key ───────────────────────────────────────── */
  const activePanelKey = useSelector((s) => s.ui.activePanelKey);
  const setActivePanelKey = (key) =>
    dispatch({ type: "SET_ACTIVE_PANEL_KEY", payload: key });

  const initialPanelSet = useRef(false);
  const isFirstNav = useRef(true);

  /* Auto-collapse on every page navigation (skip initial mount) */
  useEffect(() => {
    if (isFirstNav.current) {
      isFirstNav.current = false;
      return;
    }
    setMobileOpen(false);
    doSetCollapsed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  /* ── menu data ──────────────────────────────────────────────── */
  useEffect(() => {
    dispatch(loadMenuFromAPI());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (menuData === null) dispatch(loadMenuFromAPI());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuData]);

  /* App modules — driven by the current Redux mode. FC users get a fixed,
     restricted module list regardless of mode/platform filters. */
  const appModules = isFcUser
    ? getFcModules(menuData?.modules || [])
    : (menuData?.modules || [])
        .filter((m) => m.isActive && m.mode === mode)
        .filter((m) => !m.platformOnly || isPlatformUser)
        .filter((m) => !m.clientOnly || !isPlatformUser)
        .sort((a, b) => a.seq - b.seq);

  /* Settings is rendered separately, pinned to the bottom of the nav rail
     right above the profile section — split it out of the regular list */
  const settingsMainModule = appModules.find(
    (m) => m.moduleCode === PC.MOD_SETTINGS,
  );
  const regularAppModules = appModules.filter(
    (m) => m.moduleCode !== PC.MOD_SETTINGS,
  );

  const allModules = appModules;

  /* ── Effect 1: open panel for module owning current route ───── */
  useEffect(() => {
    if (!allModules.length) return;
    if (initialPanelSet.current) return;

    for (const mod of allModules) {
      const leafRoutes = getModuleLeafRoutes(mod, pageAccess);
      if (leafRoutes.includes(currentPath)) {
        const directPages = (mod.directPages || []).filter(
          (p) => p.isActive && isAccessible(p, pageAccess),
        );
        const hasChildren =
          (mod.submodules || []).some((s) => s.isActive) ||
          directPages.length > 1;

        // Small screens: the flyout covers most of the page, so don't
        // auto-open it on load — the rail item still shows as active.
        if (hasChildren && window.innerWidth >= 992) {
          const key = `module-${mod.id}`;
          setActivePanelKey(key);
          doSetCollapsed(false);
          for (const sub of mod.submodules || []) {
            const subPages = (sub.pages || []).filter(
              (p) => p.isActive && isAccessible(p, pageAccess),
            );
            if (subPages.some((p) => p.route === currentPath)) {
              dispatch({
                type: "SET_SIDEBAR_OPEN_KEYS",
                payload: [`sub-${sub.id}`],
              });
              break;
            }
          }
        }
        initialPanelSet.current = true;
        return;
      }
    }
    initialPanelSet.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allModules.length, mode]);

  /* ── Effect 2: sync submodule accordion on navigation ───────── */
  useEffect(() => {
    if (
      !activePanelKey ||
      typeof activePanelKey !== "string" ||
      !allModules.length
    )
      return;
    const modId = activePanelKey.replace("module-", "");
    const mod = allModules.find((m) => String(m.id) === modId);
    if (!mod) return;

    for (const sub of mod.submodules || []) {
      const subKey = `sub-${sub.id}`;
      const subPages = (sub.pages || []).filter(
        (p) => p.isActive && isAccessible(p, pageAccess),
      );
      if (subPages.some((p) => p.route === currentPath)) {
        if (!openKeys.includes(subKey)) {
          dispatch({ type: "SET_SIDEBAR_OPEN_KEYS", payload: [subKey] });
        }
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, activePanelKey]);

  /* ── handlers ───────────────────────────────────────────────── */
  const handleModuleClick = (modKey, module) => {
    const directPages = (module.directPages || []).filter(
      (p) => p.isActive && isAccessible(p, pageAccess),
    );
    const isSingleFlat =
      !(module.submodules || []).some((s) => s.isActive) &&
      directPages.length <= 1;

    if (isSingleFlat) {
      setActivePanelKey(null);
      return;
    }

    if (collapsed) doSetCollapsed(false);
    setActivePanelKey(activePanelKey === modKey ? null : modKey);
    dispatch({ type: "SET_SIDEBAR_OPEN_KEYS", payload: [] });
  };

  const toggleSubKey = (subKey) => {
    const next = openKeys.includes(subKey)
      ? openKeys.filter((k) => k !== subKey)
      : [...openKeys, subKey];
    dispatch({ type: "SET_SIDEBAR_OPEN_KEYS", payload: next });
  };

  const handleLogout = () => {
    dispatch(authLogout());
    navigate("/");
  };

  /* ── profile dropdown ───────────────────────────────────────── */
  const profileMenuItems = [
    { key: "profile", icon: <UserOutlined />, label: "Customise Profile" },
    { key: "password", icon: <LockOutlined />, label: "Change Password" },
    { type: "divider" },
    { key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true },
  ];

  const onProfileMenuClick = ({ key }) => {
    if (key === "profile") navigate("/profile", { state: { tab: "profile" } });
    if (key === "password") navigate("/change-password");
    if (key === "logout") handleLogout();
  };

  /* ── derived ────────────────────────────────────────────────── */
  const activeModule = allModules.find(
    (m) => `module-${m.id}` === activePanelKey,
  );
  const panelOpen = !!activeModule;
  // Inside the mobile drawer the rail always shows labels (expanded)
  const railCollapsed = isMobile ? false : collapsed;
  const panelLeft = railCollapsed ? 96 : 212;

  /* ── reusable module rail item ──────────────────────────────── */
  const renderModuleItem = (module) => {
    const modKey = `module-${module.id}`;
    const leafRoutes = getModuleLeafRoutes(module, pageAccess);
    const isRouteActive = leafRoutes.includes(currentPath);
    const isPanelActive = activePanelKey === modKey;
    const isActive = activePanelKey ? isPanelActive : isRouteActive;

    const directPages = (module.directPages || [])
      .filter((p) => p.isActive && isAccessible(p, pageAccess))
      .sort((a, b) => a.seq - b.seq);
    const isSingleFlat =
      !(module.submodules || []).some((s) => s.isActive) &&
      directPages.length === 1;
    const hasChildren =
      (module.submodules || []).some((s) => s.isActive) ||
      directPages.length > 1;

    const itemContent = (
      <>
        <span className="sb-rail-icon">{getIcon(module.icon)}</span>
        <span className="sb-rail-label">{module.name}</span>
        {hasChildren && (
          <span
            className="sb-rail-chevron"
            style={{
              display: "inline-flex",
              transition: "transform 0.2s ease",
              // Closed points right, open flips to point left
              transform: isPanelActive ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <RightOutlined />
          </span>
        )}
      </>
    );

    if (isSingleFlat) {
      return (
        <Tooltip
          key={modKey}
          title={module.name}
          placement="right"
          mouseEnterDelay={0.4}
        >
          <NavLink
            to={directPages[0].route}
            className={({ isActive: a }) =>
              `sb-rail-item${a && !activePanelKey ? " active" : ""}`
            }
            onClick={() => setActivePanelKey(null)}
          >
            {itemContent}
          </NavLink>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        key={modKey}
        title={module.name}
        placement="right"
        mouseEnterDelay={0.4}
      >
        <button
          className={`sb-rail-item${isActive ? " active" : ""}`}
          onClick={() => handleModuleClick(modKey, module)}
        >
          {itemContent}
        </button>
      </Tooltip>
    );
  };

  /* ── submenu panel (portaled to body) ──────────────────────── */
  const renderSubmenuPanel = () => {
    if (!activeModule) return null;

    return createPortal(
      <>
        <div className="sb-backdrop" onClick={() => setActivePanelKey(null)} />
        <div className="sb-submenu" style={{ left: panelLeft }}>
          <button
            className="sb-panel-close-btn"
            onClick={() => setActivePanelKey(null)}
            title="Close panel"
            aria-label="Close panel"
          >
            <LeftOutlined />
          </button>
          <div className="sb-panel-body">
            {(activeModule.directPages || [])
              .filter((p) => p.isActive && isAccessible(p, pageAccess))
              .sort((a, b) => a.seq - b.seq)
              .map((p) => (
                <Tooltip
                  key={p.id}
                  title={p.name}
                  placement="right"
                  mouseEnterDelay={0.3}
                >
                  <NavLink
                    to={p.route}
                    className={({ isActive }) =>
                      `sb-panel-item${isActive ? " active" : ""}`
                    }
                    onClick={() => setActivePanelKey(null)}
                  >
                    <span className="sb-dot" />
                    <span className="sb-panel-item-label">{p.name}</span>
                  </NavLink>
                </Tooltip>
              ))}

            {(activeModule.submodules || [])
              .filter((s) => s.isActive)
              .sort((a, b) => a.seq - b.seq)
              .map((sub) => {
                const subKey = `sub-${sub.id}`;
                const subPages = (sub.pages || [])
                  .filter((p) => p.isActive && isAccessible(p, pageAccess))
                  .sort((a, b) => a.seq - b.seq);
                if (!subPages.length) return null;

                const isSubOpen = openKeys.includes(subKey);
                const isSubActive = subPages.some(
                  (p) => p.route === currentPath,
                );

                return (
                  <div key={subKey} className="sb-submodule">
                    <Tooltip
                      title={sub.name}
                      placement="right"
                      mouseEnterDelay={0.3}
                    >
                      <button
                        className={`sb-subhead${isSubActive ? " active" : ""}`}
                        onClick={() => toggleSubKey(subKey)}
                      >
                        <span className="sb-subhead-label">{sub.name}</span>
                        <span className="sb-chevron">
                          {isSubOpen ? <UpOutlined /> : <DownOutlined />}
                        </span>
                      </button>
                    </Tooltip>
                    {isSubOpen && (
                      <div className="sb-sub-pages">
                        {subPages.map((p) => (
                          <Tooltip
                            key={p.id}
                            title={p.name}
                            placement="right"
                            mouseEnterDelay={0.3}
                          >
                            <NavLink
                              to={p.route}
                              className={({ isActive }) =>
                                `sb-panel-item nested${isActive ? " active" : ""}`
                              }
                              onClick={() => setActivePanelKey(null)}
                            >
                              <span className="sb-dot" />
                              <span className="sb-panel-item-label">
                                {p.name}
                              </span>
                            </NavLink>
                          </Tooltip>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </>,
      document.body,
    );
  };

  /* ═══════════════════ RENDER ════════════════════════════════ */
  if (menuLoading) {
    return <aside className={`sidebar${!railCollapsed ? " expanded" : ""}`} />;
  }

  return (
    <>
      {/* ── Brand — top-left, on the topbar's white band (same height and
          bottom border), so the header reads as one continuous bar and the
          dark rail starts below it. Width tracks the rail (collapsed /
          expanded); on small screens it also holds the drawer's burger. ── */}
      <div
        className={`sb-topbrand${!railCollapsed ? " expanded" : ""}`}
      >
        {isMobile && (
          <button
            type="button"
            className="sb-burger-btn"
            onClick={() => {
              setMobileOpen(!mobileOpen);
              setActivePanelKey(null);
            }}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <CloseOutlined /> : <MenuOutlined />}
          </button>
        )}
        {/* Logo sits directly on the white band — no tinted pill — so any
            org's uploaded logo (opaque or transparent background) blends in */}
        <div className="sb-brand-box">
          {/* Platform: T-Ops logo (its T mark when collapsed, in the
              Traidster gradient). Client: its own logo/name — the T-Ops name
              shows in the top bar instead (AppLayout PortalName) */}
          {isPlatformUser ? (
            railCollapsed ? (
              <span
                className="sb-brand-tmark"
                role="img"
                aria-label="T-Ops"
                style={{
                  WebkitMaskImage: `url(${TOpsMark})`,
                  maskImage: `url(${TOpsMark})`,
                }}
              />
            ) : (
              <img
                className="sb-brand-logo sb-brand-logo--tops"
                src={TOpsLogo}
                alt="T-Ops"
              />
            )
          ) : railCollapsed ? (
            <span className="sb-brand-initial">
              {String(brandName)[0].toUpperCase()}
            </span>
          ) : transparentBrandLogo ? (
            <img
              className="sb-brand-logo"
              src={transparentBrandLogo}
              alt={brandName}
            />
          ) : (
            // Org has no logo uploaded — show its name instead of a broken image
            <span className="sb-brand-fullname">{brandName}</span>
          )}
        </div>
      </div>

      {isMobile && mobileOpen && (
        <div
          className="sb-mobile-backdrop"
          onClick={() => {
            setMobileOpen(false);
            setActivePanelKey(null);
          }}
        />
      )}

      <aside
        className={`sidebar${!railCollapsed ? " expanded" : ""}${isMobile && mobileOpen ? " mobile-open" : ""}`}
      >
        {/* ── Navigation rail (scrollable) ── */}
        <nav className="sb-rail-nav">
          {/* App modules */}
          {regularAppModules.map(renderModuleItem)}

          {/* Settings — pinned to the bottom of the nav rail (margin-top:auto),
              right above the profile section, instead of sorting inline */}
          {settingsMainModule && (
            <div className="sb-settings-section">
              {renderModuleItem(settingsMainModule)}
            </div>
          )}
        </nav>

        {/* ── Profile — pinned to sidebar bottom ── */}
        <div className="sb-profile-section">
          <Dropdown
            menu={{ items: profileMenuItems, onClick: onProfileMenuClick }}
            trigger={["click"]}
            placement="topLeft"
            overlayStyle={{ minWidth: 200 }}
            overlayClassName="sb-profile-menu"
          >
            <Tooltip title={userName} placement="right" mouseEnterDelay={0.4}>
              <div className="sb-profile-inner" style={{ cursor: "pointer" }}>
                <div className="sb-avatar-circle">{userInitial}</div>
                <div className="sb-profile-info">
                  <span className="sb-profile-name">{userName}</span>
                </div>
              </div>
            </Tooltip>
          </Dropdown>
          <Tooltip
            title="Powered by Traidster"
            placement="right"
            mouseEnterDelay={0.4}
          >
            {/* Same label + wordmark in both states — CSS stacks them in the
                narrow collapsed rail */}
            <div className="sb-powered-by">
              <span className="sb-powered-by-label">Powered by</span>
              <img
                src={TraidsterLogoDark}
                alt="Traidster"
                className="sb-powered-by-logo"
              />
            </div>
          </Tooltip>
        </div>
      </aside>

      {/* Collapse toggle (desktop/tablet only — the burger drives mobile) */}
      {!isMobile && (
        <button
          className={`sb-collapse-btn${!collapsed ? " expanded" : ""}`}
          onClick={toggleCollapse}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <RightOutlined /> : <LeftOutlined />}
        </button>
      )}

      {/* Submenu panel — portaled for both app and settings modules */}
      {panelOpen && renderSubmenuPanel()}
    </>
  );
};

export default Sidebar;
